
import { QuizQuestion } from './enhancedQuizValidator';

export interface StandardQuizTemplate {
  moduleId: string;
  courseType: string;
  questions: QuizQuestion[];
  passThreshold: number;
  timeLimit?: number;
}

export class StandardizedQuizGenerator {
  
  public generateQuizForModule(moduleId: string, courseType: string, moduleTitle?: string): StandardQuizTemplate {
    const questions = this.getQuestionsForCourseType(courseType, moduleId, moduleTitle);
    
    return {
      moduleId,
      courseType,
      questions,
      passThreshold: 75,
      timeLimit: 600 // 10 minutes
    };
  }

  private getQuestionsForCourseType(courseType: string, moduleId: string, moduleTitle?: string): QuizQuestion[] {
    switch (courseType.toLowerCase()) {
      // Programming Languages
      case 'sql':
      case 'sql-phase1':
        return this.generateSQLQuestions(moduleId, moduleTitle);
      case 'react':
        return this.generateReactQuestions(moduleId, moduleTitle);
      case 'javascript':
        return this.generateJavaScriptQuestions(moduleId, moduleTitle);
      case 'python':
        return this.generatePythonQuestions(moduleId, moduleTitle);
      case 'java':
        return this.generateJavaQuestions(moduleId, moduleTitle);
      case 'rust':
        return this.generateRustQuestions(moduleId, moduleTitle);
      case 'csharp':
        return this.generateCSharpQuestions(moduleId, moduleTitle);
      case 'dotnet':
        return this.generateDotNetQuestions(moduleId, moduleTitle);
      
      // Cloud Platforms
      case 'aws':
        return this.generateAWSQuestions(moduleId, moduleTitle);
      case 'azure':
        return this.generateAzureQuestions(moduleId, moduleTitle);
      case 'gcp':
        return this.generateGCPQuestions(moduleId, moduleTitle);
      
      // AI/ML
      case 'ai':
        return this.generateAIQuestions(moduleId, moduleTitle);
      case 'datascience':
      case 'data-science':
        return this.generateDataScienceQuestions(moduleId, moduleTitle);
      case 'ml':
        return this.generateMLQuestions(moduleId, moduleTitle);
      
      // Database & Data
      case 'database':
        return this.generateDatabaseQuestions(moduleId, moduleTitle);
      case 'dataengineering':
      case 'data-engineering':
        return this.generateDataEngineeringQuestions(moduleId, moduleTitle);
      case 'etl':
        return this.generateETLQuestions(moduleId, moduleTitle);
      
      // DevOps & Infrastructure
      case 'devops':
        return this.generateDevOpsQuestions(moduleId, moduleTitle);
      case 'infrastructure':
        return this.generateInfrastructureQuestions(moduleId, moduleTitle);
      case 'kubernetes':
        return this.generateKubernetesQuestions(moduleId, moduleTitle);
      case 'serverless-edge':
        return this.generateServerlessQuestions(moduleId, moduleTitle);
      case 'edge-computing':
        return this.generateEdgeComputingQuestions(moduleId, moduleTitle);
      
      // Backend & Security
      case 'backend':
        return this.generateBackendQuestions(moduleId, moduleTitle);
      case 'security':
        return this.generateSecurityQuestions(moduleId, moduleTitle);
      case 'appsec':
        return this.generateAppSecQuestions(moduleId, moduleTitle);
      
      // Frontend & Mobile
      case 'mobile':
        return this.generateMobileQuestions(moduleId, moduleTitle);
      case 'frontend-advanced':
        return this.generateFrontendAdvancedQuestions(moduleId, moduleTitle);
      case 'modern-frontend':
        return this.generateModernFrontendQuestions(moduleId, moduleTitle);
      case 'cross-platform':
        return this.generateCrossPlatformQuestions(moduleId, moduleTitle);
      
      // Testing & Quality
      case 'testing-modern':
        return this.generateTestingModernQuestions(moduleId, moduleTitle);
      case 'selenium':
        return this.generateSeleniumQuestions(moduleId, moduleTitle);
      
      // Emerging Tech
      case 'blockchain':
        return this.generateBlockchainQuestions(moduleId, moduleTitle);
      case 'web3':
        return this.generateWeb3Questions(moduleId, moduleTitle);
      case 'rpa':
        return this.generateRPAQuestions(moduleId, moduleTitle);
      case 'no-code':
        return this.generateNoCodeQuestions(moduleId, moduleTitle);
      case 'green-tech':
        return this.generateGreenTechQuestions(moduleId, moduleTitle);
      
      // Data Visualization & BI
      case 'powerbi':
        return this.generatePowerBIQuestions(moduleId, moduleTitle);
      case 'tableau':
        return this.generateTableauQuestions(moduleId, moduleTitle);
      
      // Game Development & Graphics
      case 'unity':
        return this.generateUnityQuestions(moduleId, moduleTitle);
      case 'unreal':
        return this.generateUnrealQuestions(moduleId, moduleTitle);
      case 'ar-vr':
        return this.generateARVRQuestions(moduleId, moduleTitle);
      case 'desktop':
        return this.generateDesktopQuestions(moduleId, moduleTitle);
      case 'graphics':
        return this.generateGraphicsQuestions(moduleId, moduleTitle);
      case 'graphics-programming':
        return this.generateGraphicsProgrammingQuestions(moduleId, moduleTitle);
      
      // Design & UX
      case 'ui-ux':
        return this.generateUIUXQuestions(moduleId, moduleTitle);
      
      // APIs & Advanced
      case 'advanced-api':
        return this.generateAdvancedAPIQuestions(moduleId, moduleTitle);
      
      // Professional Development
      case 'enterprise-training':
        return this.generateEnterpriseTrainingQuestions(moduleId, moduleTitle);
      case 'professional-development':
        return this.generateProfessionalDevelopmentQuestions(moduleId, moduleTitle);
      
      default:
        return this.generateGenericQuestions(moduleId, moduleTitle);
    }
  }

  private generateSQLQuestions(moduleId: string, moduleTitle?: string): QuizQuestion[] {
    return [
      {
        id: `${moduleId}-q1`,
        question: "What is the primary purpose of the SELECT statement in SQL?",
        options: [
          "To retrieve data from one or more tables",
          "To insert new records into a table",
          "To update existing records",
          "To delete records from a table"
        ],
        correctAnswer: 0,
        difficulty: 'easy',
        explanation: "The SELECT statement is used to query and retrieve data from database tables.",
        category: 'sql-basics',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q2`,
        question: "Which SQL clause is used to filter records?",
        options: [
          "ORDER BY",
          "GROUP BY",
          "WHERE",
          "HAVING"
        ],
        correctAnswer: 2,
        difficulty: 'easy',
        explanation: "The WHERE clause is used to filter records based on specified conditions.",
        category: 'sql-basics',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q3`,
        question: "What type of relationship exists when one record in Table A relates to multiple records in Table B?",
        options: [
          "One-to-One",
          "One-to-Many",
          "Many-to-Many",
          "Self-referencing"
        ],
        correctAnswer: 1,
        difficulty: 'medium',
        explanation: "A One-to-Many relationship occurs when one record in the parent table can relate to multiple records in the child table.",
        category: 'database-design',
        points: 15,
        type: 'multiple-choice'
      }
    ];
  }

  private generateReactQuestions(moduleId: string, moduleTitle?: string): QuizQuestion[] {
    return [
      {
        id: `${moduleId}-q1`,
        question: "What is JSX in React?",
        options: [
          "A JavaScript library",
          "A syntax extension for JavaScript",
          "A CSS framework",
          "A database query language"
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: "JSX is a syntax extension for JavaScript that allows you to write HTML-like code in your React components.",
        category: 'react-basics',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q2`,
        question: "Which hook is used to manage component state in functional components?",
        options: [
          "useEffect",
          "useState",
          "useContext",
          "useReducer"
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: "useState is the primary hook for managing local state in functional React components.",
        category: 'react-hooks',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q3`,
        question: "What happens when you call setState in a React class component?",
        options: [
          "The component immediately re-renders",
          "The component schedules a re-render",
          "The state is updated synchronously",
          "Nothing happens"
        ],
        correctAnswer: 1,
        difficulty: 'medium',
        explanation: "setState schedules a re-render of the component. The actual update may be batched and happen asynchronously.",
        category: 'react-lifecycle',
        points: 15,
        type: 'multiple-choice'
      }
    ];
  }

  private generateJavaScriptQuestions(moduleId: string, moduleTitle?: string): QuizQuestion[] {
    return [
      {
        id: `${moduleId}-q1`,
        question: "What is the difference between 'let' and 'var' in JavaScript?",
        options: [
          "There is no difference",
          "let has block scope, var has function scope",
          "var has block scope, let has function scope",
          "let is older than var"
        ],
        correctAnswer: 1,
        difficulty: 'medium',
        explanation: "let has block scope and cannot be redeclared in the same scope, while var has function scope and can be redeclared.",
        category: 'javascript-basics',
        points: 15,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q2`,
        question: "What does the following code return: typeof null?",
        options: [
          "'null'",
          "'undefined'",
          "'object'",
          "'boolean'"
        ],
        correctAnswer: 2,
        difficulty: 'hard',
        explanation: "typeof null returns 'object' due to a historical bug in JavaScript that has been preserved for compatibility.",
        category: 'javascript-quirks',
        points: 20,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q3`,
        question: "Which method is used to add elements to the end of an array?",
        options: [
          "unshift()",
          "push()",
          "pop()",
          "shift()"
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: "push() adds one or more elements to the end of an array and returns the new length.",
        category: 'javascript-arrays',
        points: 10,
        type: 'multiple-choice'
      }
    ];
  }

  private generatePythonQuestions(moduleId: string, moduleTitle?: string): QuizQuestion[] {
    return [
      {
        id: `${moduleId}-q1`,
        question: "What is the correct way to define a function in Python?",
        options: [
          "function myFunc():",
          "def myFunc():",
          "func myFunc():",
          "define myFunc():"
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: "In Python, functions are defined using the 'def' keyword followed by the function name and parameters.",
        category: 'python-basics',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q2`,
        question: "Which data type is mutable in Python?",
        options: [
          "tuple",
          "string",
          "list",
          "int"
        ],
        correctAnswer: 2,
        difficulty: 'medium',
        explanation: "Lists are mutable in Python, meaning their contents can be changed after creation. Tuples and strings are immutable.",
        category: 'python-data-types',
        points: 15,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q3`,
        question: "What is the output of: print(len('Hello'))?",
        options: [
          "4",
          "5",
          "6",
          "Error"
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: "The len() function returns the number of characters in the string 'Hello', which is 5.",
        category: 'python-functions',
        points: 10,
        type: 'multiple-choice'
      }
    ];
  }

  private generatePowerBIQuestions(moduleId: string, moduleTitle?: string): QuizQuestion[] {
    return [
      {
        id: `${moduleId}-q1`,
        question: "What is DAX in Power BI?",
        options: [
          "A data visualization tool",
          "A formula language for calculations",
          "A data connection protocol",
          "A report template"
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: "DAX (Data Analysis Expressions) is a formula language used in Power BI for creating custom calculations and measures.",
        category: 'powerbi-basics',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q2`,
        question: "Which Power BI component is used for data modeling?",
        options: [
          "Power BI Service",
          "Power BI Desktop",
          "Power BI Mobile",
          "Power BI Gateway"
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: "Power BI Desktop is the primary tool for data modeling, creating relationships, and building reports.",
        category: 'powerbi-tools',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q3`,
        question: "What is the purpose of relationships in Power BI data models?",
        options: [
          "To improve performance",
          "To connect related data from different tables",
          "To create visualizations",
          "To store data"
        ],
        correctAnswer: 1,
        difficulty: 'medium',
        explanation: "Relationships in Power BI connect related data from different tables, enabling cross-table analysis and calculations.",
        category: 'powerbi-modeling',
        points: 15,
        type: 'multiple-choice'
      }
    ];
  }

  private generateAIQuestions(moduleId: string, moduleTitle?: string): QuizQuestion[] {
    return [
      {
        id: `${moduleId}-q1`,
        question: "What is machine learning?",
        options: [
          "A type of computer hardware",
          "A subset of AI that learns from data",
          "A programming language",
          "A database technology"
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: "Machine learning is a subset of artificial intelligence that enables systems to learn and improve from data without being explicitly programmed.",
        category: 'ai-basics',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q2`,
        question: "What is the difference between supervised and unsupervised learning?",
        options: [
          "Supervised uses labeled data, unsupervised uses unlabeled data",
          "Supervised is faster than unsupervised",
          "Unsupervised uses labeled data, supervised uses unlabeled data",
          "There is no difference"
        ],
        correctAnswer: 0,
        difficulty: 'medium',
        explanation: "Supervised learning uses labeled training data to predict outcomes, while unsupervised learning finds patterns in unlabeled data.",
        category: 'ai-types',
        points: 15,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q3`,
        question: "What is a neural network?",
        options: [
          "A computer network",
          "A mathematical model inspired by the brain",
          "A type of database",
          "A programming framework"
        ],
        correctAnswer: 1,
        difficulty: 'medium',
        explanation: "A neural network is a mathematical model inspired by biological neural networks, used for pattern recognition and learning.",
        category: 'ai-concepts',
        points: 15,
        type: 'multiple-choice'
      }
    ];
  }

  private generateDataScienceQuestions(moduleId: string, moduleTitle?: string): QuizQuestion[] {
    return [
      {
        id: `${moduleId}-q1`,
        question: "What is the primary purpose of data cleaning in data science?",
        options: [
          "To visualize data",
          "To remove or correct inaccurate data",
          "To store data",
          "To analyze data"
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: "Data cleaning involves identifying and correcting or removing inaccurate, incomplete, or irrelevant data to improve data quality.",
        category: 'data-preprocessing',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q2`,
        question: "Which Python library is commonly used for data manipulation and analysis?",
        options: [
          "NumPy",
          "Pandas",
          "Matplotlib",
          "Scikit-learn"
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: "Pandas is the most popular Python library for data manipulation and analysis, providing data structures and tools for working with structured data.",
        category: 'data-tools',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q3`,
        question: "What is exploratory data analysis (EDA)?",
        options: [
          "Building predictive models",
          "Initial investigation of data to discover patterns",
          "Cleaning data",
          "Deploying models"
        ],
        correctAnswer: 1,
        difficulty: 'medium',
        explanation: "EDA is the initial investigation of data to discover patterns, spot anomalies, and understand the data's characteristics before formal modeling.",
        category: 'data-analysis',
        points: 15,
        type: 'multiple-choice'
      }
    ];
  }

  private generateMLQuestions(moduleId: string, moduleTitle?: string): QuizQuestion[] {
    return [
      {
        id: `${moduleId}-q1`,
        question: "What is overfitting in machine learning?",
        options: [
          "When a model performs poorly on training data",
          "When a model performs well on training data but poorly on new data",
          "When a model trains too quickly",
          "When a model uses too little data"
        ],
        correctAnswer: 1,
        difficulty: 'medium',
        explanation: "Overfitting occurs when a model learns the training data too well, including noise, making it perform poorly on new, unseen data.",
        category: 'ml-concepts',
        points: 15,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q2`,
        question: "What is cross-validation used for?",
        options: [
          "Data cleaning",
          "Model evaluation and selection",
          "Feature extraction",
          "Data visualization"
        ],
        correctAnswer: 1,
        difficulty: 'medium',
        explanation: "Cross-validation is used to evaluate model performance and select the best model by testing on multiple subsets of the data.",
        category: 'ml-evaluation',
        points: 15,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q3`,
        question: "What is feature engineering?",
        options: [
          "Building hardware for ML",
          "Creating or modifying input variables for better model performance",
          "Training models",
          "Collecting data"
        ],
        correctAnswer: 1,
        difficulty: 'hard',
        explanation: "Feature engineering involves creating, selecting, or transforming input variables (features) to improve machine learning model performance.",
        category: 'ml-preprocessing',
        points: 20,
        type: 'multiple-choice'
      }
    ];
  }

  private generateGenericQuestions(moduleId: string, moduleTitle?: string): QuizQuestion[] {
    // Generate course-specific questions based on courseType patterns
    const courseTypeFromModuleId = this.inferCourseTypeFromModuleId(moduleId);
    
    if (courseTypeFromModuleId !== 'generic') {
      return this.getQuestionsForCourseType(courseTypeFromModuleId, moduleId, moduleTitle);
    }
    
    return [
      {
        id: `${moduleId}-q1`,
        question: `What is the main concept covered in the "${moduleTitle || 'current'}" module?`,
        options: [
          "Basic programming concepts and fundamentals",
          "Advanced algorithms and data structures", 
          "Best practices and design patterns",
          "Framework-specific implementations"
        ],
        correctAnswer: 0,
        difficulty: 'easy',
        explanation: "This module introduces fundamental concepts that serve as building blocks for more advanced topics.",
        category: 'fundamentals',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q2`,
        question: "Which approach represents a best practice in software development?",
        options: [
          "Writing code quickly without documentation",
          "Creating well-structured, readable, and maintainable code",
          "Using complex solutions for simple problems",
          "Avoiding code reviews and testing"
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: "Well-structured, maintainable code is always considered a best practice in software development.",
        category: 'best-practices',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q3`,
        question: "What should you do when you encounter an error?",
        options: [
          "Ignore it and continue",
          "Analyze the error message and debug systematically",
          "Restart everything",
          "Ask someone else to fix it"
        ],
        correctAnswer: 1,
        difficulty: 'medium',
        explanation: "Systematic debugging by analyzing error messages is the proper approach to problem-solving in programming.",
        category: 'problem-solving',
        points: 15,
        type: 'multiple-choice'
      }
    ];
  }
  
  /**
   * Infer course type from module ID patterns
   */
  private inferCourseTypeFromModuleId(moduleId: string): string {
    const lowerModuleId = moduleId.toLowerCase();
    
    if (lowerModuleId.includes('react') || lowerModuleId.includes('jsx') || lowerModuleId.includes('hook')) return 'react';
    if (lowerModuleId.includes('sql') || lowerModuleId.includes('database') || lowerModuleId.includes('query')) return 'sql';
    if (lowerModuleId.includes('python') || lowerModuleId.includes('py-')) return 'python';
    if (lowerModuleId.includes('javascript') || lowerModuleId.includes('js-') || lowerModuleId.includes('dom')) return 'javascript';
    if (lowerModuleId.includes('aws') || lowerModuleId.includes('ec2') || lowerModuleId.includes('s3')) return 'aws';
    if (lowerModuleId.includes('azure') || lowerModuleId.includes('microsoft')) return 'azure';
    if (lowerModuleId.includes('gcp') || lowerModuleId.includes('google-cloud')) return 'gcp';
    if (lowerModuleId.includes('java') || lowerModuleId.includes('spring')) return 'java';
    if (lowerModuleId.includes('dotnet') || lowerModuleId.includes('csharp') || lowerModuleId.includes('c#')) return 'dotnet';
    if (lowerModuleId.includes('powerbi') || lowerModuleId.includes('power-bi')) return 'powerbi';
    if (lowerModuleId.includes('tableau')) return 'tableau';
    if (lowerModuleId.includes('ai') || lowerModuleId.includes('machine-learning') || lowerModuleId.includes('ml-')) return 'ai';
    if (lowerModuleId.includes('datascience') || lowerModuleId.includes('data-science')) return 'datascience';
    if (lowerModuleId.includes('dataengineering') || lowerModuleId.includes('data-engineering')) return 'dataengineering';
    
    return 'generic';
  }
  
  /**
   * Generate questions for additional course types
   */
  private generateAWSQuestions(moduleId: string, moduleTitle?: string): QuizQuestion[] {
    return [
      {
        id: `${moduleId}-q1`,
        question: "What is Amazon Web Services (AWS)?",
        options: [
          "A programming language",
          "A cloud computing platform",
          "A database management system",
          "A web browser"
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: "AWS is Amazon's comprehensive cloud computing platform offering various services including compute, storage, and databases.",
        category: 'aws-fundamentals',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q2`,
        question: "Which AWS service is primarily used for scalable compute capacity?",
        options: [
          "S3",
          "RDS",
          "EC2",
          "Lambda"
        ],
        correctAnswer: 2,
        difficulty: 'easy',
        explanation: "Amazon EC2 (Elastic Compute Cloud) provides resizable compute capacity in the cloud.",
        category: 'aws-services',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q3`,
        question: "What is the benefit of using cloud computing over traditional infrastructure?",
        options: [
          "Higher upfront costs",
          "Limited scalability",
          "Pay-as-you-use pricing and scalability",
          "Requires more maintenance"
        ],
        correctAnswer: 2,
        difficulty: 'medium',
        explanation: "Cloud computing offers pay-as-you-use pricing, easy scalability, and reduced maintenance overhead compared to traditional infrastructure.",
        category: 'cloud-benefits',
        points: 15,
        type: 'multiple-choice'
      }
    ];
  }
  
  private generateJavaQuestions(moduleId: string, moduleTitle?: string): QuizQuestion[] {
    return [
      {
        id: `${moduleId}-q1`,
        question: "What is Java?",
        options: [
          "A markup language",
          "An object-oriented programming language",
          "A database system",
          "A web server"
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: "Java is a popular object-oriented programming language known for its 'write once, run anywhere' philosophy.",
        category: 'java-fundamentals',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q2`,
        question: "What does 'Write Once, Run Anywhere' mean in Java?",
        options: [
          "Java code runs only on specific machines",
          "Java bytecode can run on any platform with JVM",
          "Java is faster than other languages",
          "Java requires recompilation for each platform"
        ],
        correctAnswer: 1,
        difficulty: 'medium',
        explanation: "Java bytecode is platform-independent and can run on any system that has a Java Virtual Machine (JVM) installed.",
        category: 'java-concepts',
        points: 15,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q3`,
        question: "Which keyword is used to define a class in Java?",
        options: [
          "class",
          "Class",
          "define",
          "object"
        ],
        correctAnswer: 0,
        difficulty: 'easy',
        explanation: "The 'class' keyword is used to define a class in Java, followed by the class name.",
        category: 'java-syntax',
        points: 10,
        type: 'multiple-choice'
      }
    ];
  }
  
  private generateAzureQuestions(moduleId: string, moduleTitle?: string): QuizQuestion[] {
    return [
      {
        id: `${moduleId}-q1`,
        question: "What is Microsoft Azure?",
        options: [
          "A programming language",
          "Microsoft's cloud computing platform",
          "A database management system",
          "A web browser"
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: "Microsoft Azure is a comprehensive cloud computing platform offering various services including compute, storage, and databases.",
        category: 'azure-fundamentals',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q2`,
        question: "Which Azure service is used for virtual machines?",
        options: [
          "Azure Blob Storage",
          "Azure SQL Database",
          "Azure Virtual Machines",
          "Azure Functions"
        ],
        correctAnswer: 2,
        difficulty: 'easy',
        explanation: "Azure Virtual Machines provide on-demand, scalable computing resources in the cloud.",
        category: 'azure-services',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q3`,
        question: "What is Azure Resource Manager (ARM)?",
        options: [
          "A monitoring tool",
          "A deployment and management service",
          "A security service",
          "A database service"
        ],
        correctAnswer: 1,
        difficulty: 'medium',
        explanation: "Azure Resource Manager is the deployment and management service for Azure that provides a management layer.",
        category: 'azure-management',
        points: 15,
        type: 'multiple-choice'
      }
    ];
  }
  
  private generateGCPQuestions(moduleId: string, moduleTitle?: string): QuizQuestion[] {
    return [
      {
        id: `${moduleId}-q1`,
        question: "What is Google Cloud Platform (GCP)?",
        options: [
          "A search engine",
          "Google's cloud computing platform",
          "A mobile operating system",
          "A web browser"
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: "Google Cloud Platform is Google's suite of cloud computing services including compute, storage, and machine learning.",
        category: 'gcp-fundamentals',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q2`,
        question: "Which GCP service is used for scalable compute?",
        options: [
          "Cloud Storage",
          "BigQuery",
          "Compute Engine",
          "Cloud SQL"
        ],
        correctAnswer: 2,
        difficulty: 'easy',
        explanation: "Google Compute Engine provides scalable virtual machines running on Google's infrastructure.",
        category: 'gcp-services',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q3`,
        question: "What is BigQuery primarily used for?",
        options: [
          "Storing files",
          "Running virtual machines",
          "Data analytics and warehousing",
          "Container orchestration"
        ],
        correctAnswer: 2,
        difficulty: 'medium',
        explanation: "BigQuery is Google's fully managed, serverless data warehouse for analytics and business intelligence.",
        category: 'gcp-analytics',
        points: 15,
        type: 'multiple-choice'
      }
    ];
  }
  
  private generateDotNetQuestions(moduleId: string, moduleTitle?: string): QuizQuestion[] {
    return [
      {
        id: `${moduleId}-q1`,
        question: "What is .NET?",
        options: [
          "A programming language",
          "A development platform and runtime",
          "A database system",
          "A web server"
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: ".NET is a free, cross-platform, open-source developer platform for building many types of applications.",
        category: 'dotnet-fundamentals',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q2`,
        question: "Which language is primarily associated with .NET?",
        options: [
          "Python",
          "Java",
          "C#",
          "JavaScript"
        ],
        correctAnswer: 2,
        difficulty: 'easy',
        explanation: "C# is the primary programming language used with .NET, though .NET supports multiple languages.",
        category: 'dotnet-languages',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q3`,
        question: "What is ASP.NET Core?",
        options: [
          "A database",
          "A cross-platform web framework",
          "A testing tool",
          "A code editor"
        ],
        correctAnswer: 1,
        difficulty: 'medium',
        explanation: "ASP.NET Core is a cross-platform, high-performance framework for building web applications and APIs.",
        category: 'dotnet-web',
        points: 15,
        type: 'multiple-choice'
      }
    ];
  }
  
  private generateTableauQuestions(moduleId: string, moduleTitle?: string): QuizQuestion[] {
    return [
      {
        id: `${moduleId}-q1`,
        question: "What is Tableau primarily used for?",
        options: [
          "Database management",
          "Data visualization and business intelligence",
          "Web development",
          "System administration"
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: "Tableau is a leading data visualization and business intelligence platform that helps people see and understand data.",
        category: 'tableau-fundamentals',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q2`,
        question: "What is a Tableau worksheet?",
        options: [
          "A data source",
          "A single view containing visualizations",
          "A dashboard",
          "A story"
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: "A worksheet is a single view that contains visualizations built from data fields in Tableau.",
        category: 'tableau-interface',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q3`,
        question: "What is the purpose of calculated fields in Tableau?",
        options: [
          "To connect to databases",
          "To create new data based on existing fields",
          "To format worksheets",
          "To publish dashboards"
        ],
        correctAnswer: 1,
        difficulty: 'medium',
        explanation: "Calculated fields allow you to create new data from existing data using formulas and functions.",
        category: 'tableau-calculations',
        points: 15,
        type: 'multiple-choice'
      }
    ];
  }
  
  private generateDataEngineeringQuestions(moduleId: string, moduleTitle?: string): QuizQuestion[] {
    return [
      {
        id: `${moduleId}-q1`,
        question: "What is data engineering?",
        options: [
          "Building machine learning models",
          "Designing and building systems for collecting and processing data",
          "Creating data visualizations",
          "Analyzing business metrics"
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: "Data engineering focuses on designing, building, and maintaining systems that collect, store, and process data at scale.",
        category: 'data-engineering-fundamentals',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q2`,
        question: "What is an ETL pipeline?",
        options: [
          "Error Testing Logic",
          "Extract, Transform, Load",
          "Efficient Transfer Layer",
          "Enterprise Technology Link"
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: "ETL stands for Extract, Transform, Load - a process that extracts data from sources, transforms it, and loads it into a destination.",
        category: 'data-pipelines',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q3`,
        question: "What is the difference between batch and stream processing?",
        options: [
          "No difference",
          "Batch processes data in groups, stream processes data in real-time",
          "Stream is slower than batch",
          "Batch is only for small datasets"
        ],
        correctAnswer: 1,
        difficulty: 'medium',
        explanation: "Batch processing handles data in large groups at scheduled intervals, while stream processing handles data in real-time as it arrives.",
        category: 'data-processing',
        points: 15,
        type: 'multiple-choice'
      }
    ];
  }

  // ==================== NEW COURSE TYPE GENERATORS ====================
  
  private generateRustQuestions(moduleId: string, moduleTitle?: string): QuizQuestion[] {
    return [
      {
        id: `${moduleId}-q1`,
        question: "What is Rust's primary focus?",
        options: [
          "Web development",
          "Memory safety without garbage collection",
          "Mobile development",
          "Data visualization"
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: "Rust focuses on memory safety, concurrency, and performance without needing a garbage collector.",
        category: 'rust-fundamentals',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q2`,
        question: "What is ownership in Rust?",
        options: [
          "A design pattern",
          "A memory management system",
          "A testing framework",
          "A package manager"
        ],
        correctAnswer: 1,
        difficulty: 'medium',
        explanation: "Ownership is Rust's unique approach to memory management that ensures memory safety at compile time.",
        category: 'rust-ownership',
        points: 15,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q3`,
        question: "Which keyword is used to create immutable variables in Rust?",
        options: [
          "const",
          "let",
          "var",
          "immut"
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: "The 'let' keyword creates immutable bindings by default in Rust.",
        category: 'rust-syntax',
        points: 10,
        type: 'multiple-choice'
      }
    ];
  }

  private generateCSharpQuestions(moduleId: string, moduleTitle?: string): QuizQuestion[] {
    return [
      {
        id: `${moduleId}-q1`,
        question: "What is C#?",
        options: [
          "A markup language",
          "An object-oriented programming language",
          "A database system",
          "A web framework"
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: "C# is a modern, object-oriented programming language developed by Microsoft.",
        category: 'csharp-fundamentals',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q2`,
        question: "What is the Common Language Runtime (CLR)?",
        options: [
          "A code editor",
          "The execution environment for .NET applications",
          "A testing framework",
          "A version control system"
        ],
        correctAnswer: 1,
        difficulty: 'medium',
        explanation: "The CLR is the virtual machine component of .NET that manages code execution.",
        category: 'csharp-runtime',
        points: 15,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q3`,
        question: "What keyword is used for single inheritance in C#?",
        options: [
          "extends",
          "inherits",
          ":",
          "implements"
        ],
        correctAnswer: 2,
        difficulty: 'easy',
        explanation: "C# uses the colon (:) operator to indicate class inheritance.",
        category: 'csharp-oop',
        points: 10,
        type: 'multiple-choice'
      }
    ];
  }

  private generateDatabaseQuestions(moduleId: string, moduleTitle?: string): QuizQuestion[] {
    return [
      {
        id: `${moduleId}-q1`,
        question: "What is a database index?",
        options: [
          "A backup of the database",
          "A data structure that improves query performance",
          "A type of table",
          "A user permission"
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: "An index is a data structure that improves the speed of data retrieval operations.",
        category: 'database-fundamentals',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q2`,
        question: "What is ACID in databases?",
        options: [
          "A type of query",
          "A set of properties for database transactions",
          "A database backup method",
          "A programming language"
        ],
        correctAnswer: 1,
        difficulty: 'medium',
        explanation: "ACID (Atomicity, Consistency, Isolation, Durability) ensures database transaction reliability.",
        category: 'database-transactions',
        points: 15,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q3`,
        question: "What is normalization?",
        options: [
          "Backing up data",
          "Organizing data to reduce redundancy",
          "Encrypting data",
          "Compressing data"
        ],
        correctAnswer: 1,
        difficulty: 'medium',
        explanation: "Normalization is the process of organizing database schema to reduce redundancy and improve data integrity.",
        category: 'database-design',
        points: 15,
        type: 'multiple-choice'
      }
    ];
  }

  private generateETLQuestions(moduleId: string, moduleTitle?: string): QuizQuestion[] {
    return [
      {
        id: `${moduleId}-q1`,
        question: "What does ETL stand for?",
        options: [
          "Error Testing Logic",
          "Extract, Transform, Load",
          "Enterprise Technology Layer",
          "Efficient Transfer Language"
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: "ETL stands for Extract, Transform, Load - the process of moving and transforming data.",
        category: 'etl-fundamentals',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q2`,
        question: "What is data transformation in ETL?",
        options: [
          "Moving data from one place to another",
          "Converting data from one format or structure to another",
          "Deleting old data",
          "Backing up data"
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: "Transformation involves converting, cleaning, and structuring data for the target system.",
        category: 'etl-transformation',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q3`,
        question: "What is incremental loading in ETL?",
        options: [
          "Loading all data every time",
          "Loading only new or changed data",
          "Loading data in random order",
          "Loading data very slowly"
        ],
        correctAnswer: 1,
        difficulty: 'medium',
        explanation: "Incremental loading processes only new or modified data since the last load, improving efficiency.",
        category: 'etl-strategies',
        points: 15,
        type: 'multiple-choice'
      }
    ];
  }

  private generateDevOpsQuestions(moduleId: string, moduleTitle?: string): QuizQuestion[] {
    return [
      {
        id: `${moduleId}-q1`,
        question: "What is DevOps?",
        options: [
          "A programming language",
          "A culture and set of practices that unify development and operations",
          "A cloud platform",
          "A database system"
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: "DevOps combines development and operations to improve collaboration and delivery speed.",
        category: 'devops-fundamentals',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q2`,
        question: "What is Continuous Integration (CI)?",
        options: [
          "Manually deploying code",
          "Automatically building and testing code changes frequently",
          "Writing documentation",
          "Monitoring production systems"
        ],
        correctAnswer: 1,
        difficulty: 'medium',
        explanation: "CI is the practice of automatically building and testing code changes frequently to catch issues early.",
        category: 'devops-ci',
        points: 15,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q3`,
        question: "What is Infrastructure as Code (IaC)?",
        options: [
          "Writing code for applications",
          "Managing infrastructure through code and automation",
          "Building physical servers",
          "Creating user interfaces"
        ],
        correctAnswer: 1,
        difficulty: 'medium',
        explanation: "IaC manages and provisions infrastructure through machine-readable definition files.",
        category: 'devops-iac',
        points: 15,
        type: 'multiple-choice'
      }
    ];
  }

  private generateInfrastructureQuestions(moduleId: string, moduleTitle?: string): QuizQuestion[] {
    return [
      {
        id: `${moduleId}-q1`,
        question: "What is cloud infrastructure?",
        options: [
          "Physical servers in your office",
          "Virtualized computing resources delivered over the internet",
          "Network cables",
          "Desktop computers"
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: "Cloud infrastructure provides on-demand access to computing resources via the internet.",
        category: 'infrastructure-fundamentals',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q2`,
        question: "What is a load balancer?",
        options: [
          "A tool for weighing servers",
          "A system that distributes network traffic across multiple servers",
          "A backup system",
          "A monitoring tool"
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: "A load balancer distributes incoming traffic across multiple servers to ensure reliability and performance.",
        category: 'infrastructure-networking',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q3`,
        question: "What is high availability in infrastructure?",
        options: [
          "Fast servers",
          "Systems designed to minimize downtime",
          "Cheap infrastructure",
          "Easy to use systems"
        ],
        correctAnswer: 1,
        difficulty: 'medium',
        explanation: "High availability ensures systems remain operational with minimal downtime through redundancy.",
        category: 'infrastructure-reliability',
        points: 15,
        type: 'multiple-choice'
      }
    ];
  }

  private generateKubernetesQuestions(moduleId: string, moduleTitle?: string): QuizQuestion[] {
    return [
      {
        id: `${moduleId}-q1`,
        question: "What is Kubernetes?",
        options: [
          "A programming language",
          "An open-source container orchestration platform",
          "A database system",
          "A web framework"
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: "Kubernetes automates deployment, scaling, and management of containerized applications.",
        category: 'kubernetes-fundamentals',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q2`,
        question: "What is a Pod in Kubernetes?",
        options: [
          "A database",
          "The smallest deployable unit that can contain one or more containers",
          "A server",
          "A network switch"
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: "A Pod is the basic execution unit in Kubernetes that encapsulates one or more containers.",
        category: 'kubernetes-concepts',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q3`,
        question: "What is a Kubernetes Service?",
        options: [
          "A customer support system",
          "An abstraction that defines how to access Pods",
          "A billing system",
          "A monitoring tool"
        ],
        correctAnswer: 1,
        difficulty: 'medium',
        explanation: "A Service provides stable networking and load balancing for accessing Pods.",
        category: 'kubernetes-networking',
        points: 15,
        type: 'multiple-choice'
      }
    ];
  }

  private generateServerlessQuestions(moduleId: string, moduleTitle?: string): QuizQuestion[] {
    return [
      {
        id: `${moduleId}-q1`,
        question: "What is serverless computing?",
        options: [
          "Computing without any servers",
          "Cloud provider manages servers while you focus on code",
          "Using only desktop computers",
          "A type of database"
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: "Serverless means the cloud provider manages infrastructure while developers focus on code.",
        category: 'serverless-fundamentals',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q2`,
        question: "What is Function as a Service (FaaS)?",
        options: [
          "A billing model",
          "Running individual functions in response to events",
          "A storage service",
          "A networking protocol"
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: "FaaS allows running code in response to events without managing servers.",
        category: 'serverless-concepts',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q3`,
        question: "What is cold start in serverless?",
        options: [
          "Starting a function in cold weather",
          "Initial delay when a function is invoked after being idle",
          "A backup system",
          "A testing strategy"
        ],
        correctAnswer: 1,
        difficulty: 'medium',
        explanation: "Cold start is the latency when invoking a function that hasn't been used recently.",
        category: 'serverless-performance',
        points: 15,
        type: 'multiple-choice'
      }
    ];
  }

  private generateEdgeComputingQuestions(moduleId: string, moduleTitle?: string): QuizQuestion[] {
    return [
      {
        id: `${moduleId}-q1`,
        question: "What is edge computing?",
        options: [
          "Computing at the edge of your desk",
          "Processing data closer to where it's generated",
          "A type of database",
          "A programming language"
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: "Edge computing processes data near the source rather than in centralized data centers.",
        category: 'edge-fundamentals',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q2`,
        question: "What is the main benefit of edge computing?",
        options: [
          "Cheaper infrastructure",
          "Reduced latency and faster response times",
          "Easier programming",
          "Better user interfaces"
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: "Edge computing reduces latency by processing data closer to the source.",
        category: 'edge-benefits',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q3`,
        question: "What is an edge device?",
        options: [
          "Any device with sharp edges",
          "A device that processes data locally near the data source",
          "A central server",
          "A database"
        ],
        correctAnswer: 1,
        difficulty: 'medium',
        explanation: "Edge devices process and analyze data locally, reducing need for cloud communication.",
        category: 'edge-architecture',
        points: 15,
        type: 'multiple-choice'
      }
    ];
  }

  private generateBackendQuestions(moduleId: string, moduleTitle?: string): QuizQuestion[] {
    return [
      {
        id: `${moduleId}-q1`,
        question: "What is backend development?",
        options: [
          "Designing user interfaces",
          "Server-side logic, databases, and APIs",
          "Mobile app development",
          "Graphic design"
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: "Backend development focuses on server-side application logic, databases, and APIs.",
        category: 'backend-fundamentals',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q2`,
        question: "What is a RESTful API?",
        options: [
          "A sleeping API",
          "An API that follows REST architectural principles",
          "A database system",
          "A frontend framework"
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: "REST APIs use HTTP methods and follow architectural principles for web services.",
        category: 'backend-apis',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q3`,
        question: "What is middleware in backend development?",
        options: [
          "Software between servers",
          "Software that processes requests between client and server",
          "A database",
          "A frontend library"
        ],
        correctAnswer: 1,
        difficulty: 'medium',
        explanation: "Middleware intercepts and processes requests/responses in the application pipeline.",
        category: 'backend-architecture',
        points: 15,
        type: 'multiple-choice'
      }
    ];
  }

  private generateSecurityQuestions(moduleId: string, moduleTitle?: string): QuizQuestion[] {
    return [
      {
        id: `${moduleId}-q1`,
        question: "What is cybersecurity?",
        options: [
          "Security for robots",
          "Protecting systems, networks, and data from digital attacks",
          "Physical security",
          "Password management only"
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: "Cybersecurity protects digital assets from unauthorized access and attacks.",
        category: 'security-fundamentals',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q2`,
        question: "What is encryption?",
        options: [
          "Deleting data",
          "Converting data into a coded format to prevent unauthorized access",
          "Backing up data",
          "Compressing data"
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: "Encryption transforms data into an unreadable format that requires a key to decrypt.",
        category: 'security-cryptography',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q3`,
        question: "What is a SQL injection attack?",
        options: [
          "Updating a database",
          "Inserting malicious SQL code to manipulate a database",
          "Creating a database",
          "Backing up a database"
        ],
        correctAnswer: 1,
        difficulty: 'medium',
        explanation: "SQL injection exploits vulnerabilities by inserting malicious SQL commands.",
        category: 'security-threats',
        points: 15,
        type: 'multiple-choice'
      }
    ];
  }

  private generateAppSecQuestions(moduleId: string, moduleTitle?: string): QuizQuestion[] {
    return [
      {
        id: `${moduleId}-q1`,
        question: "What is application security?",
        options: [
          "Physical security of computers",
          "Measures to protect applications from threats",
          "User interface design",
          "Code formatting"
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: "Application security involves measures to improve security at the application level.",
        category: 'appsec-fundamentals',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q2`,
        question: "What is OWASP?",
        options: [
          "A programming language",
          "An organization focused on web application security",
          "A database system",
          "A cloud platform"
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: "OWASP (Open Web Application Security Project) provides resources for application security.",
        category: 'appsec-standards',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q3`,
        question: "What is Cross-Site Scripting (XSS)?",
        options: [
          "A legitimate coding technique",
          "Injecting malicious scripts into web pages",
          "A CSS framework",
          "A JavaScript library"
        ],
        correctAnswer: 1,
        difficulty: 'medium',
        explanation: "XSS attacks inject malicious scripts into trusted websites to attack users.",
        category: 'appsec-vulnerabilities',
        points: 15,
        type: 'multiple-choice'
      }
    ];
  }

  private generateMobileQuestions(moduleId: string, moduleTitle?: string): QuizQuestion[] {
    return [
      {
        id: `${moduleId}-q1`,
        question: "What is mobile app development?",
        options: [
          "Building websites",
          "Creating applications for mobile devices",
          "Desktop development",
          "Database management"
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: "Mobile development creates applications specifically for smartphones and tablets.",
        category: 'mobile-fundamentals',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q2`,
        question: "What is the difference between native and hybrid apps?",
        options: [
          "No difference",
          "Native is platform-specific, hybrid runs on multiple platforms",
          "Native is slower",
          "Hybrid is always better"
        ],
        correctAnswer: 1,
        difficulty: 'medium',
        explanation: "Native apps are built for specific platforms, hybrid apps use web technologies for multiple platforms.",
        category: 'mobile-approaches',
        points: 15,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q3`,
        question: "What is React Native?",
        options: [
          "A database",
          "A framework for building mobile apps using React",
          "A web browser",
          "An operating system"
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: "React Native allows building native mobile apps using React and JavaScript.",
        category: 'mobile-frameworks',
        points: 10,
        type: 'multiple-choice'
      }
    ];
  }

  private generateFrontendAdvancedQuestions(moduleId: string, moduleTitle?: string): QuizQuestion[] {
    return [
      {
        id: `${moduleId}-q1`,
        question: "What is the Virtual DOM?",
        options: [
          "A real DOM",
          "A lightweight copy of the actual DOM",
          "A database",
          "A server"
        ],
        correctAnswer: 1,
        difficulty: 'medium',
        explanation: "The Virtual DOM is an in-memory representation used for efficient UI updates.",
        category: 'frontend-advanced-concepts',
        points: 15,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q2`,
        question: "What is code splitting?",
        options: [
          "Writing code in multiple files",
          "Breaking code into smaller bundles loaded on demand",
          "Deleting unused code",
          "Copying code"
        ],
        correctAnswer: 1,
        difficulty: 'medium',
        explanation: "Code splitting improves performance by loading only necessary code initially.",
        category: 'frontend-performance',
        points: 15,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q3`,
        question: "What is Server-Side Rendering (SSR)?",
        options: [
          "Rendering only on client",
          "Generating HTML on the server before sending to client",
          "A database technique",
          "A CSS method"
        ],
        correctAnswer: 1,
        difficulty: 'hard',
        explanation: "SSR generates HTML on the server for better SEO and initial load performance.",
        category: 'frontend-rendering',
        points: 20,
        type: 'multiple-choice'
      }
    ];
  }

  private generateModernFrontendQuestions(moduleId: string, moduleTitle?: string): QuizQuestion[] {
    return [
      {
        id: `${moduleId}-q1`,
        question: "What is a Progressive Web App (PWA)?",
        options: [
          "A native mobile app",
          "A web app with native app-like features",
          "A desktop application",
          "A database"
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: "PWAs are web applications that provide app-like experiences including offline capability.",
        category: 'modern-frontend-concepts',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q2`,
        question: "What is Web Assembly (WASM)?",
        options: [
          "A markup language",
          "A binary instruction format for the web",
          "A CSS framework",
          "A database"
        ],
        correctAnswer: 1,
        difficulty: 'medium',
        explanation: "WebAssembly allows running high-performance code in web browsers.",
        category: 'modern-frontend-technologies',
        points: 15,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q3`,
        question: "What is the purpose of a service worker?",
        options: [
          "Managing employees",
          "Enabling offline functionality and caching",
          "Database queries",
          "User authentication"
        ],
        correctAnswer: 1,
        difficulty: 'medium',
        explanation: "Service workers are scripts that enable offline features, push notifications, and background sync.",
        category: 'modern-frontend-pwa',
        points: 15,
        type: 'multiple-choice'
      }
    ];
  }

  private generateCrossPlatformQuestions(moduleId: string, moduleTitle?: string): QuizQuestion[] {
    return [
      {
        id: `${moduleId}-q1`,
        question: "What is cross-platform development?",
        options: [
          "Building for one platform only",
          "Creating apps that run on multiple platforms from a single codebase",
          "Hardware development",
          "Database design"
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: "Cross-platform development creates applications for multiple platforms using shared code.",
        category: 'cross-platform-fundamentals',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q2`,
        question: "What is Flutter?",
        options: [
          "A database",
          "A UI framework for building cross-platform apps",
          "A web server",
          "An operating system"
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: "Flutter is Google's UI toolkit for building natively compiled apps across platforms.",
        category: 'cross-platform-frameworks',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q3`,
        question: "What is the main advantage of cross-platform development?",
        options: [
          "Slower development",
          "Code reuse across platforms",
          "Higher costs",
          "More complexity"
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: "Cross-platform development allows sharing code across platforms, reducing development time.",
        category: 'cross-platform-benefits',
        points: 10,
        type: 'multiple-choice'
      }
    ];
  }

  private generateTestingModernQuestions(moduleId: string, moduleTitle?: string): QuizQuestion[] {
    return [
      {
        id: `${moduleId}-q1`,
        question: "What is unit testing?",
        options: [
          "Testing the entire application",
          "Testing individual components or functions in isolation",
          "Testing user interfaces",
          "Testing databases"
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: "Unit testing verifies that individual units of code work correctly in isolation.",
        category: 'testing-fundamentals',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q2`,
        question: "What is Test-Driven Development (TDD)?",
        options: [
          "Testing after development",
          "Writing tests before writing code",
          "Not writing tests",
          "Manual testing only"
        ],
        correctAnswer: 1,
        difficulty: 'medium',
        explanation: "TDD involves writing tests first, then writing code to make those tests pass.",
        category: 'testing-methodologies',
        points: 15,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q3`,
        question: "What is integration testing?",
        options: [
          "Testing individual functions",
          "Testing how different parts of the system work together",
          "Testing user interfaces",
          "Performance testing"
        ],
        correctAnswer: 1,
        difficulty: 'medium',
        explanation: "Integration testing verifies that different modules or services work together correctly.",
        category: 'testing-types',
        points: 15,
        type: 'multiple-choice'
      }
    ];
  }

  private generateSeleniumQuestions(moduleId: string, moduleTitle?: string): QuizQuestion[] {
    return [
      {
        id: `${moduleId}-q1`,
        question: "What is Selenium?",
        options: [
          "A programming language",
          "An automated testing framework for web applications",
          "A database",
          "A web server"
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: "Selenium is a popular open-source framework for automating web browser testing.",
        category: 'selenium-fundamentals',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q2`,
        question: "What is a Selenium WebDriver?",
        options: [
          "A car driver",
          "An interface for browser automation",
          "A database driver",
          "A network driver"
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: "WebDriver is an interface that controls web browsers programmatically.",
        category: 'selenium-webdriver',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q3`,
        question: "What are locators in Selenium?",
        options: [
          "GPS coordinates",
          "Ways to identify elements on a web page",
          "Server locations",
          "File paths"
        ],
        correctAnswer: 1,
        difficulty: 'medium',
        explanation: "Locators are strategies to find and interact with elements on web pages (ID, class, XPath, etc.).",
        category: 'selenium-locators',
        points: 15,
        type: 'multiple-choice'
      }
    ];
  }

  private generateBlockchainQuestions(moduleId: string, moduleTitle?: string): QuizQuestion[] {
    return [
      {
        id: `${moduleId}-q1`,
        question: "What is blockchain?",
        options: [
          "A type of database",
          "A distributed ledger technology",
          "A programming language",
          "A web framework"
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: "Blockchain is a distributed ledger that records transactions across multiple computers.",
        category: 'blockchain-fundamentals',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q2`,
        question: "What is a smart contract?",
        options: [
          "A legal document",
          "Self-executing code on the blockchain",
          "A database query",
          "A network protocol"
        ],
        correctAnswer: 1,
        difficulty: 'medium',
        explanation: "Smart contracts are self-executing programs that run on blockchain when conditions are met.",
        category: 'blockchain-contracts',
        points: 15,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q3`,
        question: "What is cryptocurrency mining?",
        options: [
          "Digging for coins",
          "Validating transactions and adding blocks to the blockchain",
          "Stealing cryptocurrency",
          "Trading cryptocurrency"
        ],
        correctAnswer: 1,
        difficulty: 'medium',
        explanation: "Mining is the process of validating transactions and securing the blockchain network.",
        category: 'blockchain-mining',
        points: 15,
        type: 'multiple-choice'
      }
    ];
  }

  private generateWeb3Questions(moduleId: string, moduleTitle?: string): QuizQuestion[] {
    return [
      {
        id: `${moduleId}-q1`,
        question: "What is Web3?",
        options: [
          "The third version of a website",
          "A decentralized internet built on blockchain",
          "A web browser",
          "A programming language"
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: "Web3 represents a decentralized internet where users own their data and digital assets.",
        category: 'web3-fundamentals',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q2`,
        question: "What is a decentralized application (dApp)?",
        options: [
          "A mobile app",
          "An application that runs on a blockchain network",
          "A desktop application",
          "A website"
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: "dApps are applications that run on decentralized networks rather than centralized servers.",
        category: 'web3-dapps',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q3`,
        question: "What is a crypto wallet?",
        options: [
          "A physical wallet",
          "A digital tool for storing and managing cryptocurrency",
          "A bank account",
          "A credit card"
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: "Crypto wallets securely store private keys needed to access and manage cryptocurrency.",
        category: 'web3-wallets',
        points: 10,
        type: 'multiple-choice'
      }
    ];
  }

  private generateRPAQuestions(moduleId: string, moduleTitle?: string): QuizQuestion[] {
    return [
      {
        id: `${moduleId}-q1`,
        question: "What is RPA (Robotic Process Automation)?",
        options: [
          "Physical robots",
          "Software that automates repetitive tasks",
          "A programming language",
          "A database system"
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: "RPA uses software bots to automate repetitive, rule-based business processes.",
        category: 'rpa-fundamentals',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q2`,
        question: "What types of tasks are best suited for RPA?",
        options: [
          "Creative tasks",
          "Repetitive, rule-based tasks",
          "Strategic planning",
          "Customer relations"
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: "RPA excels at automating repetitive, predictable tasks that follow clear rules.",
        category: 'rpa-use-cases',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q3`,
        question: "What is attended vs unattended RPA?",
        options: [
          "No difference",
          "Attended works with humans, unattended runs independently",
          "Attended is faster",
          "Unattended requires supervision"
        ],
        correctAnswer: 1,
        difficulty: 'medium',
        explanation: "Attended RPA assists human workers, while unattended RPA operates independently.",
        category: 'rpa-types',
        points: 15,
        type: 'multiple-choice'
      }
    ];
  }

  private generateNoCodeQuestions(moduleId: string, moduleTitle?: string): QuizQuestion[] {
    return [
      {
        id: `${moduleId}-q1`,
        question: "What is no-code development?",
        options: [
          "Development without computers",
          "Building applications using visual interfaces without writing code",
          "A programming language",
          "A database system"
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: "No-code platforms allow creating applications through visual interfaces and drag-and-drop.",
        category: 'nocode-fundamentals',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q2`,
        question: "What is the main benefit of no-code platforms?",
        options: [
          "More complex code",
          "Faster development with less technical knowledge",
          "Better performance",
          "More expensive"
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: "No-code enables faster app development by non-technical users through visual tools.",
        category: 'nocode-benefits',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q3`,
        question: "What is the difference between no-code and low-code?",
        options: [
          "No difference",
          "No-code requires no coding, low-code allows some coding",
          "Low-code is faster",
          "No-code is always better"
        ],
        correctAnswer: 1,
        difficulty: 'medium',
        explanation: "No-code requires no programming, while low-code allows custom code for flexibility.",
        category: 'nocode-comparison',
        points: 15,
        type: 'multiple-choice'
      }
    ];
  }

  private generateGreenTechQuestions(moduleId: string, moduleTitle?: string): QuizQuestion[] {
    return [
      {
        id: `${moduleId}-q1`,
        question: "What is green technology in computing?",
        options: [
          "Technology painted green",
          "Environmentally sustainable computing practices",
          "Outdoor computing",
          "Plant-based computers"
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: "Green tech focuses on reducing environmental impact through sustainable computing practices.",
        category: 'greentech-fundamentals',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q2`,
        question: "What is energy-efficient computing?",
        options: [
          "Using old computers",
          "Optimizing systems to use less energy",
          "Working faster",
          "Using more servers"
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: "Energy-efficient computing minimizes power consumption through optimization and design.",
        category: 'greentech-efficiency',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q3`,
        question: "What is carbon-aware computing?",
        options: [
          "Computing with carbon fiber",
          "Scheduling workloads when renewable energy is available",
          "Using black computers",
          "Avoiding computers"
        ],
        correctAnswer: 1,
        difficulty: 'medium',
        explanation: "Carbon-aware computing optimizes workloads based on energy source carbon intensity.",
        category: 'greentech-carbon',
        points: 15,
        type: 'multiple-choice'
      }
    ];
  }

  private generateUnityQuestions(moduleId: string, moduleTitle?: string): QuizQuestion[] {
    return [
      {
        id: `${moduleId}-q1`,
        question: "What is Unity?",
        options: [
          "A programming language",
          "A game development engine",
          "A database",
          "A web framework"
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: "Unity is a cross-platform game engine used for 2D and 3D game development.",
        category: 'unity-fundamentals',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q2`,
        question: "What programming language is primarily used in Unity?",
        options: [
          "Python",
          "C#",
          "Java",
          "JavaScript"
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: "Unity uses C# as its primary scripting language.",
        category: 'unity-programming',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q3`,
        question: "What is a GameObject in Unity?",
        options: [
          "A game objective",
          "The fundamental object in Unity scenes",
          "A player character",
          "A game level"
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: "GameObjects are the base entities in Unity that can have components attached.",
        category: 'unity-concepts',
        points: 10,
        type: 'multiple-choice'
      }
    ];
  }

  private generateUnrealQuestions(moduleId: string, moduleTitle?: string): QuizQuestion[] {
    return [
      {
        id: `${moduleId}-q1`,
        question: "What is Unreal Engine?",
        options: [
          "A car engine",
          "A game development engine by Epic Games",
          "A database system",
          "A web framework"
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: "Unreal Engine is a powerful game engine known for high-quality graphics.",
        category: 'unreal-fundamentals',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q2`,
        question: "What is Blueprints in Unreal Engine?",
        options: [
          "Building plans",
          "A visual scripting system",
          "A texture system",
          "A lighting system"
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: "Blueprints is Unreal's visual scripting system that allows creating gameplay without code.",
        category: 'unreal-blueprints',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q3`,
        question: "What programming language does Unreal Engine use?",
        options: [
          "Python",
          "C++",
          "Java",
          "JavaScript"
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: "Unreal Engine uses C++ for high-performance game development.",
        category: 'unreal-programming',
        points: 10,
        type: 'multiple-choice'
      }
    ];
  }

  private generateARVRQuestions(moduleId: string, moduleTitle?: string): QuizQuestion[] {
    return [
      {
        id: `${moduleId}-q1`,
        question: "What is the difference between AR and VR?",
        options: [
          "No difference",
          "AR overlays digital on real world, VR creates entirely virtual environment",
          "AR is older than VR",
          "VR is only for gaming"
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: "AR augments reality with digital elements, while VR creates fully immersive virtual worlds.",
        category: 'ar-vr-fundamentals',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q2`,
        question: "What is Mixed Reality (MR)?",
        options: [
          "A broken reality",
          "A blend of physical and digital worlds",
          "Just another name for VR",
          "A database technology"
        ],
        correctAnswer: 1,
        difficulty: 'medium',
        explanation: "MR combines real and virtual worlds where physical and digital objects interact.",
        category: 'ar-vr-concepts',
        points: 15,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q3`,
        question: "What is 6DoF in VR?",
        options: [
          "6 Days of Fun",
          "Six Degrees of Freedom for movement tracking",
          "6 Displays of Features",
          "A VR headset model"
        ],
        correctAnswer: 1,
        difficulty: 'medium',
        explanation: "6DoF tracks movement in all directions (forward/back, up/down, left/right, and rotation).",
        category: 'ar-vr-technology',
        points: 15,
        type: 'multiple-choice'
      }
    ];
  }

  private generateDesktopQuestions(moduleId: string, moduleTitle?: string): QuizQuestion[] {
    return [
      {
        id: `${moduleId}-q1`,
        question: "What is desktop application development?",
        options: [
          "Web development",
          "Creating applications that run on desktop operating systems",
          "Mobile development",
          "Game development"
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: "Desktop development creates applications for Windows, macOS, and Linux operating systems.",
        category: 'desktop-fundamentals',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q2`,
        question: "What is Electron?",
        options: [
          "A particle",
          "A framework for building desktop apps with web technologies",
          "A database",
          "An operating system"
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: "Electron allows building cross-platform desktop apps using JavaScript, HTML, and CSS.",
        category: 'desktop-frameworks',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q3`,
        question: "What is the advantage of native desktop applications?",
        options: [
          "Slower performance",
          "Better performance and system integration",
          "Harder to develop",
          "No advantages"
        ],
        correctAnswer: 1,
        difficulty: 'medium',
        explanation: "Native apps have better performance and deeper OS integration than web-based alternatives.",
        category: 'desktop-native',
        points: 15,
        type: 'multiple-choice'
      }
    ];
  }

  private generateGraphicsQuestions(moduleId: string, moduleTitle?: string): QuizQuestion[] {
    return [
      {
        id: `${moduleId}-q1`,
        question: "What is computer graphics?",
        options: [
          "Drawing on paper",
          "Creating and manipulating visual content using computers",
          "Printing documents",
          "Photography"
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: "Computer graphics involves creating, storing, and manipulating visual content digitally.",
        category: 'graphics-fundamentals',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q2`,
        question: "What is the difference between raster and vector graphics?",
        options: [
          "No difference",
          "Raster uses pixels, vector uses mathematical equations",
          "Vector uses pixels",
          "Raster is always better"
        ],
        correctAnswer: 1,
        difficulty: 'medium',
        explanation: "Raster graphics use pixel grids, vector graphics use mathematical paths that scale infinitely.",
        category: 'graphics-types',
        points: 15,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q3`,
        question: "What is rendering in computer graphics?",
        options: [
          "Deleting graphics",
          "Generating an image from a model",
          "Saving graphics",
          "Editing graphics"
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: "Rendering is the process of generating a final image from 2D or 3D models.",
        category: 'graphics-rendering',
        points: 10,
        type: 'multiple-choice'
      }
    ];
  }

  private generateGraphicsProgrammingQuestions(moduleId: string, moduleTitle?: string): QuizQuestion[] {
    return [
      {
        id: `${moduleId}-q1`,
        question: "What is OpenGL?",
        options: [
          "A programming language",
          "A cross-platform graphics API",
          "A database",
          "An operating system"
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: "OpenGL is a cross-platform API for rendering 2D and 3D graphics.",
        category: 'graphics-programming-apis',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q2`,
        question: "What is a shader?",
        options: [
          "Window blinds",
          "A program that runs on the GPU for rendering",
          "A 3D model",
          "A texture"
        ],
        correctAnswer: 1,
        difficulty: 'medium',
        explanation: "Shaders are programs that run on the GPU to control rendering pipeline stages.",
        category: 'graphics-programming-shaders',
        points: 15,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q3`,
        question: "What is the graphics pipeline?",
        options: [
          "A physical pipe",
          "The sequence of steps to render graphics",
          "A storage system",
          "A network protocol"
        ],
        correctAnswer: 1,
        difficulty: 'medium',
        explanation: "The graphics pipeline is the sequence of processing stages from 3D data to 2D pixels.",
        category: 'graphics-programming-pipeline',
        points: 15,
        type: 'multiple-choice'
      }
    ];
  }

  private generateUIUXQuestions(moduleId: string, moduleTitle?: string): QuizQuestion[] {
    return [
      {
        id: `${moduleId}-q1`,
        question: "What is the difference between UI and UX?",
        options: [
          "No difference",
          "UI is visual design, UX is overall user experience",
          "UI is better than UX",
          "UX is only for mobile"
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: "UI focuses on visual elements, while UX encompasses the entire user experience and interaction.",
        category: 'ui-ux-fundamentals',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q2`,
        question: "What is a wireframe?",
        options: [
          "An electrical wire",
          "A basic visual guide of a design layout",
          "A finished design",
          "A coded website"
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: "Wireframes are low-fidelity sketches showing the basic structure and layout of a design.",
        category: 'ui-ux-design-process',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q3`,
        question: "What is usability testing?",
        options: [
          "Testing code",
          "Observing users interact with a product to identify issues",
          "Testing hardware",
          "Database testing"
        ],
        correctAnswer: 1,
        difficulty: 'medium',
        explanation: "Usability testing observes real users to identify usability problems and improvement opportunities.",
        category: 'ui-ux-testing',
        points: 15,
        type: 'multiple-choice'
      }
    ];
  }

  private generateAdvancedAPIQuestions(moduleId: string, moduleTitle?: string): QuizQuestion[] {
    return [
      {
        id: `${moduleId}-q1`,
        question: "What is GraphQL?",
        options: [
          "A database",
          "A query language for APIs",
          "A programming language",
          "A web framework"
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: "GraphQL is a query language that allows clients to request exactly the data they need.",
        category: 'api-graphql',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q2`,
        question: "What is API rate limiting?",
        options: [
          "Making APIs slower",
          "Controlling the number of API requests in a time period",
          "Deleting APIs",
          "Testing APIs"
        ],
        correctAnswer: 1,
        difficulty: 'medium',
        explanation: "Rate limiting prevents abuse by controlling how many requests a client can make.",
        category: 'api-management',
        points: 15,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q3`,
        question: "What is API versioning?",
        options: [
          "Deleting old APIs",
          "Managing different versions of an API",
          "Testing APIs",
          "Documenting APIs"
        ],
        correctAnswer: 1,
        difficulty: 'medium',
        explanation: "API versioning allows maintaining multiple API versions for backward compatibility.",
        category: 'api-versioning',
        points: 15,
        type: 'multiple-choice'
      }
    ];
  }

  private generateEnterpriseTrainingQuestions(moduleId: string, moduleTitle?: string): QuizQuestion[] {
    return [
      {
        id: `${moduleId}-q1`,
        question: "What is enterprise software?",
        options: [
          "Software for small businesses",
          "Large-scale software for organizations",
          "Mobile apps",
          "Games"
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: "Enterprise software serves the needs of large organizations rather than individual users.",
        category: 'enterprise-fundamentals',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q2`,
        question: "What is ERP (Enterprise Resource Planning)?",
        options: [
          "A programming language",
          "Integrated software managing business processes",
          "A database",
          "A web framework"
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: "ERP systems integrate core business processes like finance, HR, and operations.",
        category: 'enterprise-systems',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q3`,
        question: "What is SOA (Service-Oriented Architecture)?",
        options: [
          "A database design",
          "An architectural pattern using services as building blocks",
          "A testing method",
          "A deployment strategy"
        ],
        correctAnswer: 1,
        difficulty: 'medium',
        explanation: "SOA structures applications as collections of loosely coupled, interoperable services.",
        category: 'enterprise-architecture',
        points: 15,
        type: 'multiple-choice'
      }
    ];
  }

  private generateProfessionalDevelopmentQuestions(moduleId: string, moduleTitle?: string): QuizQuestion[] {
    return [
      {
        id: `${moduleId}-q1`,
        question: "What is continuous learning in professional development?",
        options: [
          "One-time training",
          "Ongoing skill development throughout a career",
          "Only classroom learning",
          "Learning only at work"
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: "Continuous learning involves regularly updating skills and knowledge throughout one's career.",
        category: 'professional-development-fundamentals',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q2`,
        question: "What is a growth mindset?",
        options: [
          "Believing abilities are fixed",
          "Believing abilities can be developed through effort",
          "Only for children",
          "A business strategy"
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: "A growth mindset believes abilities and intelligence can be developed through dedication and hard work.",
        category: 'professional-mindset',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: `${moduleId}-q3`,
        question: "What is emotional intelligence in the workplace?",
        options: [
          "Being emotional",
          "Understanding and managing emotions effectively",
          "Avoiding emotions",
          "Technical skills"
        ],
        correctAnswer: 1,
        difficulty: 'medium',
        explanation: "Emotional intelligence involves recognizing, understanding, and managing emotions in oneself and others.",
        category: 'professional-soft-skills',
        points: 15,
        type: 'multiple-choice'
      }
    ];
  }
}

export const standardizedQuizGenerator = new StandardizedQuizGenerator();
